/**
 * i18n Terouma — module maison, zéro dépendance.
 *
 * Principe : un dictionnaire à plat par domaine dans i18n/locales/*.js
 * (chaque fichier exporte { fr: {...}, en: {...} } avec des clés préfixées
 * "domaine.cle"), fusionnés ici. Les pages utilisent :
 *
 *   const { t, lang, setLang } = useI18n();
 *   t("pay.title")                      → chaîne traduite
 *   t("pay.hello", { name: "Sarah" })   → interpolation {name}
 *
 * Langue choisie : localStorage "terouma_lang" > langue du navigateur
 * (fr → français, tout le reste → anglais). Fallback d'une clé manquante :
 * EN → FR → la clé elle-même (on n'affiche jamais un trou).
 *
 * ⚠️ RÈGLE : on ne traduit que l'AFFICHAGE. Les valeurs envoyées à l'API
 * (motifs de dons, méthodes de paiement, statuts) restent inchangées.
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

import common from "./locales/common";
import pay from "./locales/pay";
import donate from "./locales/donate";
import portal from "./locales/portal";
import verify from "./locales/verify";
import declare from "./locales/declare";

const MODULES = [common, pay, donate, portal, verify, declare];

const DICTS = {
  fr: Object.assign({}, ...MODULES.map((m) => m.fr || {})),
  en: Object.assign({}, ...MODULES.map((m) => m.en || {})),
};

const STORAGE_KEY = "terouma_lang";

export function detectLang() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "fr" || saved === "en") return saved;
  } catch { /* stockage indisponible (navigation privée) */ }
  const nav = (typeof navigator !== "undefined" && navigator.language) || "fr";
  return nav.toLowerCase().startsWith("fr") ? "fr" : "en";
}

function interpolate(str, vars) {
  if (!vars) return str;
  return str.replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined ? String(vars[k]) : m));
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectLang);

  const setLang = useCallback((l) => {
    const next = l === "en" ? "en" : "fr";
    setLangState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
    try { document.documentElement.lang = next; } catch { /* ignore */ }
  }, []);

  const t = useCallback(
    (key, vars) => {
      const raw = DICTS[lang]?.[key] ?? DICTS.fr[key] ?? key;
      return interpolate(raw, vars);
    },
    [lang],
  );

  const value = useMemo(() => ({ t, lang, setLang }), [t, lang, setLang]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Page rendue hors provider (sécurité) : français statique, pas de crash.
    return {
      t: (key, vars) => interpolate(DICTS.fr[key] ?? key, vars),
      lang: "fr",
      setLang: () => {},
    };
  }
  return ctx;
}
